import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { RespondentColumnName, RespondentColumnNameDTO } from "../types/respondentColumnName";
/* eslint-disable import/export */
export abstract class GetSurveyRuleValueOptionsRPC implements
  LeoRPC<GetSurveyRuleValueOptionsRPC.Request, GetSurveyRuleValueOptionsRPC.Response, GetSurveyRuleValueOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyRuleValueOptionsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyRuleValueOptionsRPC.Response, GetSurveyRuleValueOptionsRPC.Errors.Errors>>;
}

export namespace GetSurveyRuleValueOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    export namespace Key {
      export interface QuestionCodeDTO {
        case: string;
        code: CodeDTO;
      }
      
      export class QuestionCode {
        readonly isKeyQuestionCode: boolean = true;
      
        readonly code: Code;
      
      
        constructor(
        code: Code
        ) {
          this.code = code;
        }
      
        static fromDTO(dto: QuestionCodeDTO): QuestionCode {
          const code = Code.fromDTO(getObject(dto, "code", InvalidRequestKeyQuestionCodeCodeError));
          return new QuestionCode(
            code
          );
        }
      
        toDTO(): QuestionCodeDTO {
          const dto: QuestionCodeDTO = {
            case: "QUESTION_CODE",
              code: this.code.toDTO()
          };
          return dto;
        }
        }
      
      QuestionCode.prototype.toString = function toString(): string {
        return `Key.QUESTION_CODE(code='${this.code}')`;
      };
      
      export interface RespondentColumnDTO {
        case: string;
        name: RespondentColumnNameDTO;
      }
      
      export class RespondentColumn {
        readonly isKeyRespondentColumn: boolean = true;
      
        readonly name: RespondentColumnName;
      
      
        constructor(
        name: RespondentColumnName
        ) {
          this.name = name;
        }
      
        static fromDTO(dto: RespondentColumnDTO): RespondentColumn {
          const name = RespondentColumnName.fromDTO(getObject(dto, "name", InvalidRequestKeyRespondentColumnNameError));
          return new RespondentColumn(
            name
          );
        }
      
        toDTO(): RespondentColumnDTO {
          const dto: RespondentColumnDTO = {
            case: "RESPONDENT_COLUMN",
              name: this.name.toDTO()
          };
          return dto;
        }
        }
      
      RespondentColumn.prototype.toString = function toString(): string {
        return `Key.RESPONDENT_COLUMN(name='${this.name}')`;
      };
      
    
      export type Key = QuestionCode | RespondentColumn;
    
      export type KeyDTO = QuestionCodeDTO | RespondentColumnDTO;
    
      export const fromDTO = (dto: KeyDTO): Key => {
        switch (dto.case) {
          case "QUESTION_CODE":
              return QuestionCode.fromDTO(dto as QuestionCodeDTO);
          case "RESPONDENT_COLUMN":
              return RespondentColumn.fromDTO(dto as RespondentColumnDTO);
          default:
            throw new InvalidRequestKeyError(
                `Case ${dto.case} is not valid case of Key.`
            );
        }
      };
    }
    
  }
  export interface RequestDTO {
    surveyId: string;
    key: RequestEnums.Key.KeyDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyRuleValueOptionsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly key: RequestEnums.Key.Key;
  
  
    constructor(
    surveyId: LeoUUID, 
    key: RequestEnums.Key.Key
    ) {
      this.surveyId = surveyId;
      this.key = key;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const key = RequestEnums.Key.fromDTO(getObject(dto, "key", InvalidRequestKeyError));
      return new Request(
        surveyId,
        key
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          key: this.key.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyRuleValueOptionsRPC(surveyId='${this.surveyId}',key='${this.key}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace ValueOption {
      export interface QuestionOptionCodeDTO {
        case: string;
        codes: CodeDTO[];
      }
      
      export class QuestionOptionCode {
        readonly isValueOptionQuestionOptionCode: boolean = true;
      
        readonly codes: Code[];
      
      
        constructor(
        codes: Code[]
        ) {
          this.codes = codes;
        }
      
        static fromDTO(dto: QuestionOptionCodeDTO): QuestionOptionCode {
          const codes = getList<CodeDTO, Code>(
            dto,
            "codes",
            Code.fromDTO,
            InvalidResponseValueOptionQuestionOptionCodeCodesError
          );
          return new QuestionOptionCode(
            codes
          );
        }
      
        toDTO(): QuestionOptionCodeDTO {
          const dto: QuestionOptionCodeDTO = {
            case: "QUESTION_OPTION_CODE",
              codes: this.codes.map((e: Code) => { return e.toDTO() })
          };
          return dto;
        }
        }
      
      QuestionOptionCode.prototype.toString = function toString(): string {
        return `ValueOption.QUESTION_OPTION_CODE(codes='${this.codes}')`;
      };
      
      
      export namespace RespondentColumnValueEnums {
        export namespace ValueType {
          export interface TooManyUniqueValuesDTO {
            case: string;
          }
          
          export class TooManyUniqueValues {
            readonly isValueTypeTooManyUniqueValues: boolean = true;
          
          
          
          
          
            toDTO(): TooManyUniqueValuesDTO {
              const dto: TooManyUniqueValuesDTO = {
                case: "TOO_MANY_UNIQUE_VALUES",
              };
              return dto;
            }
            }
          
          TooManyUniqueValues.prototype.toString = function toString(): string {
            return `ValueType.TOO_MANY_UNIQUE_VALUES()`;
          };
          
          export interface UniqueValuesDTO {
            case: string;
            values: string[];
          }
          
          export class UniqueValues {
            readonly isValueTypeUniqueValues: boolean = true;
          
            readonly values: string[];
          
          
            constructor(
            values: string[]
            ) {
              this.values = values;
            }
          
            static fromDTO(dto: UniqueValuesDTO): UniqueValues {
              const values = getList<string, string>(
                dto,
                "values",
                (e: string) => e,
                InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesValuesError
              );
              return new UniqueValues(
                values
              );
            }
          
            toDTO(): UniqueValuesDTO {
              const dto: UniqueValuesDTO = {
                case: "UNIQUE_VALUES",
                  values: this.values
              };
              return dto;
            }
            }
          
          UniqueValues.prototype.toString = function toString(): string {
            return `ValueType.UNIQUE_VALUES(values='${this.values}')`;
          };
          
        
          export type ValueType = TooManyUniqueValues | UniqueValues;
        
          export type ValueTypeDTO = TooManyUniqueValuesDTO | UniqueValuesDTO;
        
          export const fromDTO = (dto: ValueTypeDTO): ValueType => {
            switch (dto.case) {
              case "TOO_MANY_UNIQUE_VALUES":
                  return new TooManyUniqueValues();
              case "UNIQUE_VALUES":
                  return UniqueValues.fromDTO(dto as UniqueValuesDTO);
              default:
                throw new InvalidResponseValueOptionRespondentColumnValueValueTypeError(
                    `Case ${dto.case} is not valid case of ValueType.`
                );
            }
          };
        }
        
      }
      export interface RespondentColumnValueDTO {
        case: string;
        valueType: RespondentColumnValueEnums.ValueType.ValueTypeDTO;
      }
      
      export class RespondentColumnValue {
        readonly isValueOptionRespondentColumnValue: boolean = true;
      
        readonly valueType: RespondentColumnValueEnums.ValueType.ValueType;
      
      
        constructor(
        valueType: RespondentColumnValueEnums.ValueType.ValueType
        ) {
          this.valueType = valueType;
        }
      
        static fromDTO(dto: RespondentColumnValueDTO): RespondentColumnValue {
          const valueType = RespondentColumnValueEnums.ValueType.fromDTO(getObject(dto, "valueType", InvalidResponseValueOptionRespondentColumnValueValueTypeError));
          return new RespondentColumnValue(
            valueType
          );
        }
      
        toDTO(): RespondentColumnValueDTO {
          const dto: RespondentColumnValueDTO = {
            case: "RESPONDENT_COLUMN_VALUE",
              valueType: this.valueType.toDTO()
          };
          return dto;
        }
        }
      
      RespondentColumnValue.prototype.toString = function toString(): string {
        return `ValueOption.RESPONDENT_COLUMN_VALUE(valueType='${this.valueType}')`;
      };
      
    
      export type ValueOption = QuestionOptionCode | RespondentColumnValue;
    
      export type ValueOptionDTO = QuestionOptionCodeDTO | RespondentColumnValueDTO;
    
      export const fromDTO = (dto: ValueOptionDTO): ValueOption => {
        switch (dto.case) {
          case "QUESTION_OPTION_CODE":
              return QuestionOptionCode.fromDTO(dto as QuestionOptionCodeDTO);
          case "RESPONDENT_COLUMN_VALUE":
              return RespondentColumnValue.fromDTO(dto as RespondentColumnValueDTO);
          default:
            throw new InvalidResponseValueOptionError(
                `Case ${dto.case} is not valid case of ValueOption.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    valueOption: ResponseEnums.ValueOption.ValueOptionDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyRuleValueOptionsResponse: boolean = true;
  
    readonly valueOption: ResponseEnums.ValueOption.ValueOption;
  
  
    constructor(
    valueOption: ResponseEnums.ValueOption.ValueOption
    ) {
      this.valueOption = valueOption;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const valueOption = ResponseEnums.ValueOption.fromDTO(getObject(dto, "valueOption", InvalidResponseValueOptionError));
      return new Response(
        valueOption
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          valueOption: this.valueOption.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyRuleValueOptions(valueOption='${this.valueOption}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidQuestionCode = "INVALID_QUESTION_CODE",
    UnsupportedQuestionType = "UNSUPPORTED_QUESTION_TYPE",
    InvalidRespondentColumnName = "INVALID_RESPONDENT_COLUMN_NAME"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyRuleValueOptionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidQuestionCodeDTO {
      code: string;
    }
    
    export class InvalidQuestionCode extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionCode: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionCode;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionCodeDTO {
        const dto: InvalidQuestionCodeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionCode.prototype.toString = function toString(): string {
      return `GetSurveyRuleValueOptionsRPC.INVALID_QUESTION_CODE()`;
    };
    
    
    export interface UnsupportedQuestionTypeDTO {
      code: string;
    }
    
    export class UnsupportedQuestionType extends LeoRPCError<RPCError> {
      readonly isUnsupportedQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnsupportedQuestionType;
        super(code);
      }
    
    
      toDTO(): UnsupportedQuestionTypeDTO {
        const dto: UnsupportedQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnsupportedQuestionType.prototype.toString = function toString(): string {
      return `GetSurveyRuleValueOptionsRPC.UNSUPPORTED_QUESTION_TYPE()`;
    };
    
    
    export interface InvalidRespondentColumnNameDTO {
      code: string;
    }
    
    export class InvalidRespondentColumnName extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentColumnName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentColumnName;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentColumnNameDTO {
        const dto: InvalidRespondentColumnNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentColumnName.prototype.toString = function toString(): string {
      return `GetSurveyRuleValueOptionsRPC.INVALID_RESPONDENT_COLUMN_NAME()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidQuestionCode | UnsupportedQuestionType | InvalidRespondentColumnName;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidQuestionCodeDTO | UnsupportedQuestionTypeDTO | InvalidRespondentColumnNameDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidQuestionCode:
          return new InvalidQuestionCode();
        case RPCError.UnsupportedQuestionType:
          return new UnsupportedQuestionType();
        case RPCError.InvalidRespondentColumnName:
          return new InvalidRespondentColumnName();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyRuleValueOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestKeyError extends GetSurveyRuleValueOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyError";
  }
}

export class InvalidRequestKeyQuestionCodeError extends InvalidRequestKeyError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyQuestionCodeError";
  }
}

export class InvalidRequestKeyQuestionCodeCodeError extends InvalidRequestKeyQuestionCodeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyQuestionCodeCodeError";
  }
}

export class InvalidRequestKeyRespondentColumnError extends InvalidRequestKeyError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyRespondentColumnError";
  }
}

export class InvalidRequestKeyRespondentColumnNameError extends InvalidRequestKeyRespondentColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyRespondentColumnNameError";
  }
}

export class InvalidResponseValueOptionError extends GetSurveyRuleValueOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionError";
  }
}

export class InvalidResponseValueOptionQuestionOptionCodeError extends InvalidResponseValueOptionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionQuestionOptionCodeError";
  }
}

export class InvalidResponseValueOptionQuestionOptionCodeCodesError extends InvalidResponseValueOptionQuestionOptionCodeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionQuestionOptionCodeCodesError";
  }
}

export class InvalidResponseValueOptionRespondentColumnValueError extends InvalidResponseValueOptionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionRespondentColumnValueError";
  }
}

export class InvalidResponseValueOptionRespondentColumnValueValueTypeError extends InvalidResponseValueOptionRespondentColumnValueError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionRespondentColumnValueValueTypeError";
  }
}

export class InvalidResponseValueOptionRespondentColumnValueValueTypeTooManyUniqueValuesError extends InvalidResponseValueOptionRespondentColumnValueValueTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionRespondentColumnValueValueTypeTooManyUniqueValuesError";
  }
}

export class InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesError extends InvalidResponseValueOptionRespondentColumnValueValueTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesError";
  }
}

export class InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesValuesError extends InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseValueOptionRespondentColumnValueValueTypeUniqueValuesValuesError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyRuleValueOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidQuestionCodeError extends GetSurveyRuleValueOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionCodeError";
  }
}

export class InvalidResponseUnsupportedQuestionTypeError extends GetSurveyRuleValueOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnsupportedQuestionTypeError";
  }
}

export class InvalidResponseInvalidRespondentColumnNameError extends GetSurveyRuleValueOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentColumnNameError";
  }
}

